import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePreferenciasAyuda } from '../ayuda/usePreferenciasAyuda';

/**
 * Menú del usuario en la barra superior (se abre desde su nombre): preferencias personales, por ahora la ayuda
 * interactiva. El cambio se ve enseguida; si no se puede guardar, vuelve atrás y avisa.
 */
export function MenuUsuario() {
  const { user } = useAuth();
  const ayuda = usePreferenciasAyuda();
  const [abierto, setAbierto] = useState(false);
  const idPanel = useId();
  const contenedor = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);

  // Escape o un clic afuera lo cierran (Escape devuelve el foco al botón).
  useEffect(() => {
    if (!abierto) return;
    const alTeclado = (e: KeyboardEvent) => { if (e.key === 'Escape') { setAbierto(false); boton.current?.focus(); } };
    const alClic = (e: MouseEvent) => { if (!contenedor.current?.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('keydown', alTeclado);
    document.addEventListener('mousedown', alClic);
    return () => { document.removeEventListener('keydown', alTeclado); document.removeEventListener('mousedown', alClic); };
  }, [abierto]);

  if (!user) return null;

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={boton}
        type="button"
        aria-expanded={abierto}
        aria-controls={idPanel}
        onClick={() => setAbierto(a => !a)}
        className="text-sm text-slate-500 hover:text-slate-700 truncate max-w-[8rem] sm:max-w-none font-medium px-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {user.username}
      </button>
      {abierto && (
        <div id={idPanel} className="absolute right-0 top-full mt-2 z-30 w-64 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-lg p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-700" id={`${idPanel}-ayuda`}>Ayuda interactiva</span>
            <button
              type="button"
              role="switch"
              aria-checked={ayuda.activa}
              aria-labelledby={`${idPanel}-ayuda`}
              onClick={() => ayuda.setActiva(!ayuda.activa)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ${ayuda.activa ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${ayuda.activa ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <p className="text-xs text-slate-500">Guías paso a paso en los formularios que la tienen.</p>
          {ayuda.activa && ayuda.ocultas.length > 0 && (
            <button type="button" onClick={() => ayuda.mostrarOcultas()} className="text-xs text-brand-600 hover:text-brand-700 font-medium">
              Volver a mostrar las guías ocultas
            </button>
          )}
        </div>
      )}
    </div>
  );
}
